import { useState, useEffect, useCallback } from 'react';
import { getAdminFees } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const feeTypeStyle = {
  withdrawal_charge: { color: '#ff6666', bg: 'rgba(200,50,50,0.12)', border: 'rgba(200,50,50,0.3)' },
};

const statusStyle = {
  pending:    { color: '#ffaa44', bg: 'rgba(255,170,68,0.12)', border: 'rgba(255,170,68,0.3)' },
  approved:   { color: '#66aaff', bg: 'rgba(102,170,255,0.12)', border: 'rgba(102,170,255,0.3)' },
  successful: { color: '#66ff99', bg: 'rgba(102,255,153,0.12)', border: 'rgba(102,255,153,0.3)' },
  rejected:   { color: '#ff6666', bg: 'rgba(200,50,50,0.12)', border: 'rgba(200,50,50,0.3)' },
  cancelled:  { color: '#888',    bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
};

const AdminFees = () => {
  const [fees, setFees] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [feeType, setFeeType] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalFees, setTotalFees] = useState(0);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setLoading(true);
    getAdminFees({ page, limit: 20, feeType, search })
      .then((res) => {
        setFees(res.data.fees);
        setTotal(res.data.total);
        setPages(res.data.pages);
        // Calculate total fees for current view
        const sum = res.data.fees.reduce((acc, f) => acc + f.feeAmount, 0);
        setTotalFees(sum);
      })
      .catch(() => setFees([]))
      .finally(() => setLoading(false));
  }, [page, feeType, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Fees History</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>
        {total} total deductions • Revenue: <span style={{ color: '#00ff88', fontWeight: 600 }}>${totalFees.toFixed(2)}</span>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {/* Search */}
        <div>
          <input
            type="text"
            placeholder="Search by user code or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: 'rgba(0,229,255,0.04)',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '10px 14px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        {/* Fee Type Filter */}
        <div style={{ maxWidth: 300 }}>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Fee Type</label>
          <select
            value={feeType}
            onChange={(e) => { setFeeType(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: '#1a1a1a',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All Fees</option>
            <option value="withdrawal_charge" style={{ background: '#1a1a1a', color: '#ff6666' }}>Withdrawal Charges (10%)</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '900px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
              {['User', 'User Code', 'Email', 'Fee Type', 'From Source', 'Amount', 'Fee', 'Status', 'Date'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
            ) : fees.length === 0 ? (
              <tr><td colSpan={9} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No fees found.</td></tr>
            ) : fees.map((f) => {
              const fts = feeTypeStyle[f.feeType] || feeTypeStyle.withdrawal_charge;
              const sts = statusStyle[f.withdrawalStatus] || statusStyle.pending;
              return (
                <tr key={f._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 13 }}>{f.user?.name}</div>
                  </td>
                  <td style={{ color: '#00e5ff', fontSize: 11, padding: '11px 14px', fontFamily: 'monospace' }}>
                    {f.user?.userCode || '—'}
                  </td>
                  <td style={{ color: '#888', fontSize: 11, padding: '11px 14px' }}>
                    {f.user?.email || '—'}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: fts.bg, border: `1px solid ${fts.border}`, color: fts.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                      {f.feeTypeLabel}
                    </span>
                  </td>
                  <td style={{ color: '#ffaa44', fontSize: 12, padding: '11px 14px', fontWeight: 600 }}>
                    {f.sourceType}
                  </td>
                  <td style={{ color: '#fff', fontWeight: 700, fontSize: 13, padding: '11px 14px' }}>
                    ${f.sourceAmount.toFixed(2)}
                  </td>
                  <td style={{ color: '#ff6666', fontWeight: 700, fontSize: 13, padding: '11px 14px' }}>
                    ${f.feeAmount.toFixed(2)}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: sts.bg, border: `1px solid ${sts.border}`, color: sts.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
                      {f.withdrawalStatus}
                    </span>
                  </td>
                  <td style={{ color: '#555', fontSize: 11, padding: '11px 14px', whiteSpace: 'nowrap' }}>
                    {fmtIST(f.createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Footer */}
      {fees.length > 0 && (
        <div style={{
          background: 'rgba(255,100,100,0.08)',
          border: '1px solid rgba(255,100,100,0.2)',
          borderRadius: 8,
          padding: '14px 18px',
          marginTop: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ color: '#888', fontSize: 13 }}>
            Showing {fees.length} of {total} records
          </div>
          <div style={{ color: '#ff6666', fontSize: 14, fontWeight: 600 }}>
            Page Total Revenue: ${totalFees.toFixed(2)}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminFees;
